// CastSidebar.tsx — Draggable pick-order panel list for Cast Layout view.
// Panels listed in pick order; drag any row to reorder. Checkboxes exclude
// panels from the cast layout without removing them from the list.

import { useRef, useState } from 'react';
import type { PanelData } from '../types/panelvar';

export interface CastPanelItem {
  panel:    PanelData;
  elevName: string;
}

interface Props {
  items:            CastPanelItem[];    // all floor panels in current pick order
  checkedIds:       Set<string>;
  onReorder:        (newOrder: string[]) => void;
  onToggle:         (id: string) => void;
  onSelectAll:      () => void;
  onDeselectAll:    () => void;
  collapsed:        boolean;
  onToggleCollapse: () => void;
}

export default function CastSidebar({
  items, checkedIds, onReorder, onToggle, onSelectAll, onDeselectAll,
  collapsed, onToggleCollapse,
}: Props) {
  const dragIdRef      = useRef<string | null>(null);
  const dragOverPosRef = useRef<'before' | 'after'>('after');
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const [dragOverPos, setDragOverPos] = useState<'before' | 'after'>('after');

  if (collapsed) {
    return (
      <div className="sidebar collapsed">
        <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Expand sidebar">›</button>
      </div>
    );
  }

  const allChecked = items.length > 0 && items.every(i => checkedIds.has(i.panel.id));

  // Assign sequential pick numbers only to checked panels, in list order
  const pickNums = new Map<string, number>();
  let num = 1;
  for (const item of items) {
    if (checkedIds.has(item.panel.id)) pickNums.set(item.panel.id, num++);
  }

  function handleDragStart(e: React.DragEvent, id: string) {
    dragIdRef.current = id;
    e.dataTransfer.effectAllowed = 'move';
    // Set dummy data so Firefox fires drag events
    e.dataTransfer.setData('text/plain', id);
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pos: 'before' | 'after' = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after';
    dragOverPosRef.current = pos;
    setDragOverId(id);
    setDragOverPos(pos);
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault();
    const srcId = dragIdRef.current;
    setDragOverId(null);
    if (!srcId || srcId === targetId) return;

    const ids    = items.map(i => i.panel.id);
    const srcIdx = ids.indexOf(srcId);
    const tgtIdx = ids.indexOf(targetId);
    if (srcIdx === -1 || tgtIdx === -1) return;

    const newIds = ids.filter(id => id !== srcId);
    let insertAt = newIds.indexOf(targetId);
    if (dragOverPosRef.current === 'after') insertAt += 1;
    newIds.splice(insertAt, 0, srcId);
    onReorder(newIds);
  }

  function handleDragEnd() {
    dragIdRef.current = null;
    setDragOverId(null);
  }

  return (
    <div className="sidebar-inner">
      <div className="sidebar-header">
        <span className="sidebar-title">Pick Order</span>
        <button className="sidebar-collapse-btn" onClick={onToggleCollapse} title="Collapse sidebar">‹</button>
      </div>

      <div className="elev-sidebar-actions">
        <button className="elev-sidebar-btn" onClick={onSelectAll}   disabled={allChecked}>All</button>
        <button className="elev-sidebar-btn" onClick={onDeselectAll} disabled={checkedIds.size === 0}>Clear</button>
      </div>

      <ul className="elev-list" style={{ paddingBottom: 8 }}>
        {items.map(item => {
          const id      = item.panel.id;
          const checked = checkedIds.has(id);
          const pickNum = pickNums.get(id);
          const isOver  = dragOverId === id;
          const mark    = item.panel.mpvar.panelMark || id;

          return (
            <li
              key={id}
              className={`elev-item elev-item-child${checked ? ' checked' : ''}`}
              style={{
                display:      'flex',
                alignItems:   'center',
                gap:          4,
                paddingLeft:  4,
                opacity:      checked ? 1 : 0.4,
                borderTop:    isOver && dragOverPos === 'before' ? '2px solid #4da6ff' : undefined,
                borderBottom: isOver && dragOverPos === 'after'  ? '2px solid #4da6ff' : undefined,
                userSelect:   'none',
                cursor:       'default',
              }}
              draggable
              onDragStart={e => handleDragStart(e, id)}
              onDragOver={e  => handleDragOver(e, id)}
              onDrop={e      => handleDrop(e, id)}
              onDragEnd={handleDragEnd}
            >
              {/* Drag handle */}
              <span
                style={{ color: '#555', fontSize: 14, flexShrink: 0, cursor: 'grab', lineHeight: 1 }}
                title="Drag to reorder"
              >
                ⣿
              </span>

              {/* Pick number badge */}
              <span style={{
                display:        'inline-flex',
                alignItems:     'center',
                justifyContent: 'center',
                width:          20,
                height:         20,
                borderRadius:   '50%',
                background:     checked ? '#2a5a8a' : '#333',
                color:          checked ? '#fff'    : '#666',
                fontSize:       10,
                fontWeight:     700,
                flexShrink:     0,
              }}>
                {pickNum ?? '—'}
              </span>

              {/* Checkbox — click only, does NOT bubble to li */}
              <input
                type="checkbox"
                className="elev-checkbox"
                checked={checked}
                onChange={() => onToggle(id)}
                onClick={e => e.stopPropagation()}
                style={{ flexShrink: 0, cursor: 'pointer' }}
              />

              {/* Panel mark — click to toggle */}
              <span
                className="elev-label"
                style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }}
                onClick={() => onToggle(id)}
              >
                {mark}
              </span>

              {/* Elevation tag */}
              <span style={{ color: '#555', fontSize: 9, flexShrink: 0, maxWidth: 52, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item.elevName}
              </span>
            </li>
          );
        })}

        {items.length === 0 && (
          <li style={{ color: '#888', fontStyle: 'italic', padding: '8px 12px', listStyle: 'none' }}>
            No panels on this floor
          </li>
        )}
      </ul>
    </div>
  );
}
